export type DiffKind = 'context' | 'added' | 'removed';

export interface DiffLine {
  kind: DiffKind;
  text: string;
  originalLine?: number;
  updatedLine?: number;
}

export interface Diff {
  lines: DiffLine[];
  added: number;
  removed: number;
}

/** Classic LCS table. Ontology files are small enough that O(n·m) is free. */
function longestCommonSubsequence(original: string[], updated: string[]): number[][] {
  const table: number[][] = Array.from(
    { length: original.length + 1 },
    () => new Array<number>(updated.length + 1).fill(0),
  );

  for (let i = original.length - 1; i >= 0; i -= 1) {
    for (let j = updated.length - 1; j >= 0; j -= 1) {
      table[i][j] = original[i] === updated[j]
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  return table;
}

function toLines(text: string): string[] {
  return text.replace(/\r\n/g, '\n').replace(/\n$/, '').split('\n');
}

export function diffText(original: string, updated: string): Diff {
  const left = toLines(original);
  const right = toLines(updated);
  const table = longestCommonSubsequence(left, right);
  const lines: DiffLine[] = [];

  let i = 0;
  let j = 0;

  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      lines.push({ kind: 'context', text: left[i], originalLine: i + 1, updatedLine: j + 1 });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      lines.push({ kind: 'removed', text: left[i], originalLine: i + 1 });
      i += 1;
    } else {
      lines.push({ kind: 'added', text: right[j], updatedLine: j + 1 });
      j += 1;
    }
  }

  while (i < left.length) {
    lines.push({ kind: 'removed', text: left[i], originalLine: i + 1 });
    i += 1;
  }

  while (j < right.length) {
    lines.push({ kind: 'added', text: right[j], updatedLine: j + 1 });
    j += 1;
  }

  return {
    lines,
    added: lines.filter((line) => line.kind === 'added').length,
    removed: lines.filter((line) => line.kind === 'removed').length,
  };
}

/** Collapses runs of unchanged lines the way a unified diff does. */
export function withContext(diff: Diff, context = 3): DiffLine[] {
  const keep = new Set<number>();

  diff.lines.forEach((line, index) => {
    if (line.kind === 'context') {
      return;
    }

    for (let offset = -context; offset <= context; offset += 1) {
      keep.add(index + offset);
    }
  });

  return diff.lines.filter((_line, index) => keep.has(index));
}
